import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Share, SquarePlus, CheckCircle2, Copy } from "lucide-react";
import { canPrompt, detectBrowser, isInstalled, onPwaChange, promptInstall } from "@/lib/pwa";

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3 rounded-xl bg-neutral-100 px-4 py-3 text-left text-sm text-neutral-800">
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-crimson text-xs font-bold text-white">{n}</span>
    <span className="leading-relaxed">{children}{Icon && <Icon size={16} className="ml-1 inline-block align-text-bottom text-crimson" />}</span>
  </li>
);

const IosGuide = () => (
  <ol data-testid="install-ios-guide" className="space-y-2">
    <Step n={1} icon={Share}>Buka halaman ini di <b>Safari</b>, lalu ketuk tombol <b>Bagikan</b></Step>
    <Step n={2} icon={SquarePlus}>Gulir ke bawah, pilih <b>Tambah ke Layar Utama</b></Step>
    <Step n={3}>Ketuk <b>Tambah</b>. Ikon <b>DA LE TANG</b> akan muncul di layar utama.</Step>
  </ol>
);

const HOME = <>Ikon <b>DA LE TANG</b> akan muncul di layar utama.</>;
const DESK = <>Ikon <b>DA LE TANG</b> akan muncul di desktop / menu Start.</>;
const GUIDES = {
  samsung: { name: "Samsung Internet", steps: [<>Ketuk menu <b>≡</b> di kanan bawah</>, <>Pilih <b>Tambahkan halaman ke</b> → <b>Layar Utama</b></>, <>Ketuk <b>Tambahkan</b>. {HOME}</>] },
  miui: { name: "Mi Browser", steps: [<>Ketuk menu <b>≡</b> atau <b>⋮</b></>, <>Pilih <b>Tambahkan ke layar utama</b></>, <>Ketuk <b>Tambahkan</b>. {HOME}</>] },
  "opera-android": { name: "Opera", steps: [<>Ketuk menu <b>⋮</b> di kanan atas</>, <>Pilih <b>Tambahkan ke…</b> → <b>Layar utama</b></>, <>Ketuk <b>Tambahkan</b>. {HOME}</>] },
  "firefox-android": { name: "Firefox", steps: [<>Ketuk menu <b>⋮</b></>, <>Pilih <b>Tambahkan ke layar utama</b> (atau <b>Instal</b>)</>, <>Ketuk <b>Tambahkan</b>. {HOME}</>] },
  "chrome-android": { name: "Chrome / Edge", steps: [<>Ketuk menu <b>⋮</b> (atau <b>···</b> di Edge)</>, <>Pilih <b>Instal aplikasi</b> atau <b>Tambahkan ke layar utama</b></>, <>Ketuk <b>Instal</b>. {HOME}</>] },
  "other-android": { name: "browser ini", steps: [<>Buka menu browser (<b>⋮</b> atau <b>≡</b>)</>, <>Pilih <b>Tambahkan ke layar utama</b> atau <b>Instal</b></>, <>Konfirmasi. {HOME}</>] },
  "chrome-desktop": { name: "Chrome", steps: [<>Klik ikon <b>Instal</b> (layar dengan panah) di ujung kanan bilah alamat</>, <>Atau buka menu <b>⋮</b> → <b>Transmisikan, simpan, dan bagikan</b> → <b>Instal halaman sebagai aplikasi</b></>, <>Klik <b>Instal</b>. {DESK}</>] },
  edge: { name: "Microsoft Edge", steps: [<>Klik ikon <b>Aplikasi tersedia</b> di kanan bilah alamat</>, <>Atau buka menu <b>···</b> → <b>Aplikasi</b> → <b>Instal situs ini sebagai aplikasi</b></>, <>Klik <b>Instal</b>. {DESK}</>] },
  "firefox-desktop": { name: "Firefox", copy: true, steps: [<>Klik ikon <b>Tambahkan ke bilah tugas</b> di kanan bilah alamat (Firefox 143 ke atas di Windows)</>, <>Konfirmasi. Aplikasi <b>DA LE TANG</b> akan muncul di taskbar dan menu Start.</>, <>Ikon tidak ada? Perbarui Firefox, atau salin tautan di bawah dan buka di <b>Microsoft Edge</b> / <b>Chrome</b>.</>] },
  "opera-desktop": { name: "Opera", copy: true, steps: [<>Opera di komputer belum bisa memasang aplikasi ini</>, <>Salin tautan di bawah, lalu buka di <b>Microsoft Edge</b> (sudah ada di Windows) atau <b>Chrome</b></>, <>Di sana tekan <b>Instal Aplikasi</b> lagi.</>] },
  "other-desktop": { name: "browser ini", copy: true, steps: [<>Cari ikon <b>Instal</b> di bilah alamat atau menu browser</>, <>Jika tidak ada, salin tautan di bawah dan buka di <b>Microsoft Edge</b> atau <b>Chrome</b></>, <>Klik <b>Instal</b>. {DESK}</>] },
};

const copyLink = async () => {
  try { await navigator.clipboard.writeText(`${window.location.origin}/install`); toast.success("Tautan disalin"); }
  catch { toast.error("Gagal menyalin, salin alamat dari bilah alamat"); }
};

const BrowserGuide = ({ kind }) => {
  const g = GUIDES[kind] || GUIDES["other-desktop"];
  return (
    <div data-testid={`install-guide-${kind}`}>
      <p className="mb-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cara memasang di {g.name}</p>
      <ol className="space-y-2">{g.steps.map((s, i) => <Step key={i} n={i + 1}>{s}</Step>)}</ol>
      {g.copy && (
        <Button data-testid="install-copy-link-btn" variant="outline" onClick={copyLink} className="mt-3 w-full font-semibold">
          <Copy size={16} className="mr-2" /> Salin Tautan
        </Button>
      )}
    </div>
  );
};

export default function Install() {
  const [installed, setInstalled] = useState(false);
  const [ready, setReady] = useState(canPrompt());
  const kind = detectBrowser();

  useEffect(() => {
    isInstalled().then(setInstalled);
    return onPwaChange(() => { setReady(canPrompt()); isInstalled().then(setInstalled); });
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-hotpot-grid p-4">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="glass w-full max-w-md rounded-3xl p-6 text-center sm:p-8 red-glow" data-testid="install-page">
        <img src="/icon-192.png" alt="DA LE TANG" className="mx-auto h-24 w-24 rounded-[22%] shadow-lg ring-1 ring-neutral-200" />
        <h1 className="mt-4 font-display text-2xl font-extrabold text-neutral-900">DA LE TANG</h1>
        <p className="mb-6 text-sm text-muted-foreground">Pasang aplikasi agar bisa dibuka langsung dari layar utama atau desktop.</p>

        {installed ? (
          <div data-testid="install-done" className="flex items-center justify-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
            <CheckCircle2 size={18} /> Aplikasi sudah terpasang di perangkat ini.
          </div>
        ) : kind === "ios" ? <IosGuide /> : ready ? (
          <Button data-testid="install-now-btn" onClick={promptInstall} className="touch-btn w-full bg-crimson text-base font-bold hover:bg-red-500">
            <Download size={18} className="mr-2" /> Pasang Sekarang
          </Button>
        ) : <BrowserGuide kind={kind} />}

        <Link to="/login" data-testid="install-back-link" className="mt-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-crimson">
          <ArrowLeft size={14} /> Kembali ke Login
        </Link>
      </motion.div>
    </div>
  );
}
